import { cn } from "@circleco/community-switcher/helpers/cn";
import type { CSSProperties, ReactNode } from "react";

import { useCommunitySwitcherContext } from "../context/CommunitySwitcherContext";
import { SWITCHER_ICON_CLASS } from "./constants";

interface CommunitySectionPresentationContentProps {
  readonly communityId: number;
  readonly isElectron?: boolean;
  readonly tooltipLabel: string;
  readonly isActive: boolean;
  readonly onElectronActivate?: () => void;
  readonly href?: string;
  readonly name?: string;
  readonly notificationCount: number;
  readonly children: ReactNode;
  readonly dragHandleRef?: (element: Element | null) => void;
  readonly dragHandleStyle?: CSSProperties;
}

export const CommunitySectionPresentationContent = ({
  communityId,
  isElectron,
  tooltipLabel,
  isActive,
  onElectronActivate,
  children,
  href,
  name,
  notificationCount,
  dragHandleRef,
  dragHandleStyle,
}: CommunitySectionPresentationContentProps) => {
  const { t } = useCommunitySwitcherContext();
  const baseInteractiveLabel = isElectron ? tooltipLabel : (name ?? "");
  const accessibleLabel =
    notificationCount > 0
      ? `${baseInteractiveLabel}, ${t("community_sidebar.unread_notifications", { count: notificationCount })}`
      : baseInteractiveLabel;

  if (isElectron) {
    return (
      <button
        ref={dragHandleRef}
        type="button"
        aria-label={accessibleLabel}
        className={cn(SWITCHER_ICON_CLASS, {
          "active border-primary switcher__active_community": isActive,
          "cursor-grab select-none": Boolean(dragHandleRef),
        })}
        style={dragHandleStyle}
        data-testid="community-switcher-link"
        onClick={onElectronActivate}
        aria-current={isActive ? "page" : undefined}
      >
        {children}
      </button>
    );
  }
  return (
    <a
      ref={dragHandleRef}
      aria-label={accessibleLabel}
      className={cn(SWITCHER_ICON_CLASS, {
        "active border-primary switcher__active_community": isActive,
        "cursor-grab select-none": Boolean(dragHandleRef),
      })}
      style={dragHandleStyle}
      data-community-switcher-nav=""
      data-community-id={communityId}
      data-testid="community-switcher-link"
      href={href}
    >
      {children}
    </a>
  );
};
